import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEye,
  faChevronLeft,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleXmark,
  faLoader,
  faRefresh,
  faUserGroup,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { openModal } from "../../store/modal/modalReducer";
import ErrorBoundary from "../../containers/ErrorBoundary";

import CustomButton from "../Helpers/CustomButton";
import { closeModal } from "../../store/modal/modalReducer";
import FileUpload from "../Forms/FileUpload";
import FileDisplay from "../Forms/FileDisplay";
import axios from "axios";
import ViewFile from "../Helpers/ViewFile";

export default function SlideoverFiles(props) {
  const dispatch = useDispatch();
  const { current_booking } = useSelector((state) => state.booking);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const [showFile, setShowFile] = useState(false);
  const [fileId, setFileId] = useState(false);
  const [fileView, setFileView] = useState(false);
  const [files, setFiles] = useState([]);
  const [untaggedFiles, setUntaggedFiles] = useState([]);
  const [applicantFilter, setApplicantFilter] = useState("all"); // 'all', 'main', 'joint'
  const [uploadAsJoint, setUploadAsJoint] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date"); // 'date' or 'name'

  // Function to fetch files directly from server
  const fetchFiles = async () => {
    if (!current_booking?.id) return;

    // Clear existing files first to force re-render
    setFiles([]);
    setUntaggedFiles([]);
    setLoading(true);

    try {
      // Add cache-busting parameter to ensure fresh data
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}booking/${current_booking.id}/files`,
        {
          params: { _t: Date.now() },
        },
      );

      // Handle the actual API response structure
      if (Array.isArray(response.data)) {
        // If response.data is directly an array of files
        setFiles(response.data);
        setUntaggedFiles([]);
      } else {
        // If response.data has nested structure
        setFiles(response.data.files || []);
        setUntaggedFiles(response.data.untagged_files || []);
      }
    } catch (error) {
      console.error("Failed to fetch files:", error);
      setFiles([]);
      setUntaggedFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch files when modal opens
  useEffect(() => {
    fetchFiles();
  }, [current_booking?.id]);

  // Auto-preview a specific file if passed via props
  useEffect(() => {
    if (props.autoPreviewFileId) {
      setFileId(props.autoPreviewFileId);
      setShowFile(true);
    }
  }, [props.autoPreviewFileId]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (fileId) {
      setPreviewLoading(true);
      setPreviewError(null);
      setShowFile(true); // Keep preview mode active while loading
      setFileView(null); // Clear old file content

      axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}preview_file`,
        data: { fileId: fileId },
      })
        .then((response) => {
          // Ensure response has valid data structure
          if (response.data && typeof response.data === "object") {
            setFileView(response.data);
            setPreviewError(null);
          } else {
            // Invalid response structure
            setFileView(null);
            setPreviewError("Invalid file data received");
          }
        })
        .catch((error) => {
          console.error("Error previewing file:", error);
          setFileView(null);
          setPreviewError("Failed to load file preview");
        })
        .finally(() => {
          setPreviewLoading(false);
        });
    }
  }, [fileId]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          dispatch(closeModal());
          setOpen(false);
        }}
      >
        <ErrorBoundary>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel
                    className={`pointer-events-auto transition-all duration-300 ease-in-out ${showFile ? "w-[90vw]" : "w-screen max-w-2xl"}`}
                  >
                    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
                      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll bg-light_background">
                        <div className="bg-dark_background py-6">
                          <div className=" px-4 sm:px-6">
                            <div className="flex items-start justify-between">
                              <Dialog.Title className="text-lg font-medium text-white">
                                Attachments for {current_booking.user.name}{" "}
                                {current_booking.user.surname}
                              </Dialog.Title>
                              <div className="ml-3 flex h-7 items-center space-x-2">
                                <button
                                  type="button"
                                  className="rounded-md text-white hover:text-gray-200 transition-colors duration-150"
                                  onClick={fetchFiles}
                                  disabled={loading}
                                  title="Refresh file list"
                                >
                                  <span className="sr-only">Refresh files</span>
                                  <FontAwesomeIcon
                                    className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                                    aria-hidden="true"
                                    icon={loading ? faLoader : faRefresh}
                                  />
                                </button>
                                <button
                                  type="button"
                                  className="rounded-md text-white hover:text-gray-200"
                                  onClick={() => dispatch(closeModal())}
                                >
                                  <span className="sr-only">Close panel</span>
                                  <FontAwesomeIcon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                    icon={faTimes}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex h-full">
                          {/* Preview Panel - Left Side */}
                          <div
                            className={`hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out ${
                              showFile
                                ? "flex-1 opacity-100"
                                : "w-0 opacity-0 overflow-hidden"
                            }`}
                          >
                            {showFile && (
                              <>
                                {/* Close Preview Header - button on right */}
                                <div className="flex items-center justify-end px-4 py-3 border-b border-gray-200 bg-gray-50">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setShowFile(false);
                                      setFileId(false);
                                      setFileView(null);
                                    }}
                                    className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
                                  >
                                    Close Preview
                                    <FontAwesomeIcon
                                      icon={faTimes}
                                      className="ml-2 h-4 w-4"
                                    />
                                  </button>
                                </div>
                                {/* File Preview Content - full width, centered */}
                                <div className="flex-1 overflow-auto p-4 flex flex-col items-center">
                                  {previewLoading ? (
                                    <div className="flex flex-col items-center justify-center h-full w-full">
                                      <FontAwesomeIcon
                                        className="h-10 w-10 animate-spin text-primary mb-4"
                                        icon={faLoader}
                                      />
                                      <span className="text-gray-500">
                                        Loading preview...
                                      </span>
                                    </div>
                                  ) : previewError ? (
                                    <div className="flex flex-col items-center justify-center h-full w-full">
                                      <FontAwesomeIcon
                                        className="h-10 w-10 text-red-400 mb-4"
                                        icon={faCircleXmark}
                                      />
                                      <span className="text-red-600 font-medium">
                                        {previewError}
                                      </span>
                                      <span className="text-gray-500 text-sm mt-2">
                                        Try selecting another file or refresh
                                        the list
                                      </span>
                                    </div>
                                  ) : fileView && !fileView.encrypted ? (
                                    <div className="w-full">
                                      <FileDisplay
                                        file={fileView}
                                        key={fileView.id}
                                        callback={true}
                                        onScreen={true}
                                      />
                                      {fileView &&
                                        fileView.type &&
                                        fileView.type.includes("pdf") && (
                                          <ViewFile file={fileView} />
                                        )}
                                      {fileView &&
                                        fileView.type &&
                                        fileView.type.includes("image") && (
                                          <img
                                            src={fileView.url}
                                            alt="preview"
                                            className="max-w-full"
                                          />
                                        )}

                                      {fileView &&
                                        fileView.type &&
                                        fileView.type.includes("audio") && (
                                          <div className="mt-4 w-full">
                                            <audio controls className="w-full">
                                              <source
                                                src={fileView.url}
                                                type={fileView.type}
                                              />
                                              Your browser does not support the
                                              audio element.
                                            </audio>
                                          </div>
                                        )}

                                      {((fileView.type &&
                                        !fileView.type.includes("image") &&
                                        !fileView.type.includes("pdf") &&
                                        !fileView.type.includes("audio")) ||
                                        !fileView.type) && (
                                        <div className="flex items-center bg-red-100 rounded-md p-4">
                                          <FontAwesomeIcon
                                            className="h-4 w-4 text-red-800 mr-4"
                                            icon={faCircleXmark}
                                          />

                                          {!fileView.type ? (
                                            <>
                                              <p className="text-red-800">
                                                Unable to determine file type.
                                              </p>
                                              <p className="text-red-800">
                                                {" "}
                                                Please try downloading the file
                                                or contact support.
                                              </p>
                                            </>
                                          ) : fileView.type.includes("null") ? (
                                            <>
                                              <p className="text-red-800">
                                                This file is corrupted.
                                              </p>
                                              <p className="text-red-800">
                                                {" "}
                                                Please request a new file and
                                                delete this one
                                              </p>
                                            </>
                                          ) : (
                                            <>
                                              <p className="text-red-800">
                                                Format cannot be translated.
                                              </p>
                                              <p className="text-red-800">
                                                {" "}
                                                Please download the file.
                                              </p>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : fileView && fileView.encrypted ? (
                                    <div className="w-full">
                                      <FileDisplay
                                        file={fileView}
                                        key={fileView.id}
                                        callback={true}
                                        onScreen={true}
                                      />
                                      <div className="flex items-center bg-red-100 rounded-md p-4">
                                        <FontAwesomeIcon
                                          className="h-4 w-4 text-red-800 mr-4"
                                          icon={faCircleXmark}
                                        />
                                        <p className="text-red-800">
                                          The File is Encrypted. Please download
                                          the file and use the password.
                                        </p>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>

                          {/* File List Panel - Right Side (25% when preview, full when no preview) */}
                          <div
                            className={`relative px-4 sm:px-6 overflow-y-auto transition-all duration-300 ease-in-out ${
                              showFile
                                ? "w-1/4 min-w-[280px] max-w-[400px] flex-shrink-0 bg-gray-50"
                                : "flex-1 pt-4"
                            }`}
                          >
                            {/* Hide upload section when in preview mode */}
                            {!showFile && (
                              <>
                                {/* Joint Application Upload Toggle */}
                                {current_booking?.joint_application == 1 && (
                                  <div className="mb-4 flex items-center justify-between bg-white rounded-md p-3 border border-gray-200">
                                    <span className="text-sm font-medium text-gray-700">
                                      Upload for:
                                    </span>
                                    <div className="flex rounded-md shadow-sm">
                                      <button
                                        type="button"
                                        onClick={() => setUploadAsJoint(false)}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                                          !uploadAsJoint
                                            ? "bg-primary text-white border-primary"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        <FontAwesomeIcon
                                          icon={faUser}
                                          className="mr-1"
                                        />
                                        Main
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setUploadAsJoint(true)}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-r border-b ${
                                          uploadAsJoint
                                            ? "bg-primary text-white border-primary"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        <FontAwesomeIcon
                                          icon={faUserGroup}
                                          className="mr-1"
                                        />
                                        Joint
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <FileUpload
                                  current_booking={current_booking}
                                  file_type=""
                                  type={props.type}
                                  tag_id={props.tag_id}
                                  onUploadComplete={fetchFiles}
                                  isJoint={uploadAsJoint}
                                />
                                <div className="relative mt-6 py-4">
                                  <div
                                    className="absolute inset-0 flex items-center"
                                    aria-hidden="true"
                                  >
                                    <div className="w-full border-t border-dark_background" />
                                  </div>
                                  <div className="relative flex justify-center">
                                    <span className="px-3 bg-light_background text-lg font-medium text-gray-600">
                                      Uploaded Files
                                    </span>
                                  </div>
                                </div>

                                {/* Search and Filter Row */}
                                <div className="mb-4 flex flex-wrap gap-2">
                                  <input
                                    type="text"
                                    placeholder="Search files..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                      setSearchQuery(e.target.value)
                                    }
                                    className="flex-1 min-w-[150px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                                  />
                                  {/* Sort Toggle */}
                                  <div
                                    className="inline-flex rounded-md shadow-sm flex-shrink-0"
                                    role="group"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => setSortBy("date")}
                                      className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                                        sortBy === "date"
                                          ? "bg-gray-600 text-white border-gray-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                      title="Sort by date (newest first)"
                                    >
                                      Date
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setSortBy("name")}
                                      className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                                        sortBy === "name"
                                          ? "bg-gray-600 text-white border-gray-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                      title="Sort by name (A-Z)"
                                    >
                                      Name
                                    </button>
                                  </div>
                                  {current_booking?.joint_application == 1 && (
                                    <div
                                      className="inline-flex rounded-md shadow-sm flex-shrink-0"
                                      role="group"
                                    >
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setApplicantFilter("all")
                                        }
                                        className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                                          applicantFilter === "all"
                                            ? "bg-primary text-white border-primary"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        All
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setApplicantFilter("main")
                                        }
                                        className={`px-3 py-2 text-sm font-medium border-t border-b ${
                                          applicantFilter === "main"
                                            ? "bg-primary text-white border-primary"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        <FontAwesomeIcon
                                          icon={faUser}
                                          className="mr-1"
                                        />
                                        Main
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setApplicantFilter("joint")
                                        }
                                        className={`px-3 py-2 text-sm font-medium rounded-r-md border ${
                                          applicantFilter === "joint"
                                            ? "bg-purple-600 text-white border-purple-600"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                                      >
                                        <FontAwesomeIcon
                                          icon={faUserGroup}
                                          className="mr-1"
                                        />
                                        Joint
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {/* Compact header with search and filter when in preview mode */}
                            {showFile && (
                              <div className="py-3 border-b border-gray-200 mb-3 space-y-2">
                                <h3 className="text-sm font-medium text-gray-700">
                                  Files
                                </h3>
                                {/* Search input */}
                                <input
                                  type="text"
                                  placeholder="Search files..."
                                  value={searchQuery}
                                  onChange={(e) =>
                                    setSearchQuery(e.target.value)
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                                />
                                {/* Applicant filter - only show if joint application */}
                                {current_booking?.joint_application == 1 && (
                                  <div className="flex rounded-md shadow-sm">
                                    <button
                                      type="button"
                                      onClick={() => setApplicantFilter("all")}
                                      className={`flex-1 px-2 py-1 text-xs font-medium rounded-l-md border ${
                                        applicantFilter === "all"
                                          ? "bg-primary text-white border-primary"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      All
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setApplicantFilter("main")}
                                      className={`flex-1 px-2 py-1 text-xs font-medium border-t border-b ${
                                        applicantFilter === "main"
                                          ? "bg-primary text-white border-primary"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faUser}
                                        className="mr-1"
                                      />
                                      Main
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setApplicantFilter("joint")
                                      }
                                      className={`flex-1 px-2 py-1 text-xs font-medium rounded-r-md border ${
                                        applicantFilter === "joint"
                                          ? "bg-purple-600 text-white border-purple-600"
                                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faUserGroup}
                                        className="mr-1"
                                      />
                                      Joint
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {files.length > 0 || untaggedFiles.length > 0 ? (
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                {untaggedFiles.filter(
                                  (f) =>
                                    (current_booking?.joint_application != 1 ||
                                      applicantFilter === "all" ||
                                      (applicantFilter === "main" &&
                                        !f.is_joint) ||
                                      (applicantFilter === "joint" &&
                                        f.is_joint)) &&
                                    (!searchQuery ||
                                      f.name
                                        ?.toLowerCase()
                                        .includes(searchQuery.toLowerCase())),
                                ).length > 0 && (
                                  <>
                                    {!showFile && (
                                      <p className="text-lg">Untracked Files</p>
                                    )}
                                    {!showFile && <br />}
                                    <ul
                                      role="list"
                                      className="border border-dark_background rounded-md divide-y divide-dark_background"
                                    >
                                      {untaggedFiles
                                        .filter(
                                          (f) =>
                                            // Hide signature files (show=0)
                                            f.show !== 0 &&
                                            (current_booking?.joint_application !=
                                              1 ||
                                              applicantFilter === "all" ||
                                              (applicantFilter === "main" &&
                                                !f.is_joint) ||
                                              (applicantFilter === "joint" &&
                                                f.is_joint)) &&
                                            (!searchQuery ||
                                              f.name
                                                ?.toLowerCase()
                                                .includes(
                                                  searchQuery.toLowerCase(),
                                                )),
                                        )
                                        .sort((a, b) =>
                                          sortBy === "date"
                                            ? new Date(b.created_at) -
                                              new Date(a.created_at)
                                            : (a.name || "").localeCompare(
                                                b.name || "",
                                              ),
                                        )
                                        .map((file) => (
                                          <FileDisplay
                                            file={file}
                                            key={file.id}
                                            setShowFile={setShowFile}
                                            showFile={showFile}
                                            setFileId={setFileId}
                                            fileId={fileId}
                                            onScreen={false}
                                            currentPreview={fileId}
                                            mapped={false}
                                            moveYear={current_booking?.year}
                                            onFileChange={fetchFiles}
                                            showJointToggle={
                                              current_booking?.joint_application ==
                                              1
                                            }
                                            compactMode={showFile}
                                          />
                                        ))}
                                    </ul>
                                    <br />
                                  </>
                                )}
                                {files.filter(
                                  (f) =>
                                    // Hide signature files (show=0)
                                    f.show !== 0 &&
                                    (current_booking?.joint_application != 1 ||
                                      applicantFilter === "all" ||
                                      (applicantFilter === "main" &&
                                        !f.is_joint) ||
                                      (applicantFilter === "joint" &&
                                        f.is_joint)) &&
                                    (!searchQuery ||
                                      f.name
                                        ?.toLowerCase()
                                        .includes(searchQuery.toLowerCase())),
                                ).length > 0 && (
                                  <>
                                    {!showFile && (
                                      <p className="text-lg">
                                        {current_booking?.year?.year ||
                                          "Current Year"}{" "}
                                        Files
                                      </p>
                                    )}
                                    {!showFile && <br />}
                                    <ul
                                      role="list"
                                      className="border border-dark_background rounded-md divide-y divide-dark_background"
                                    >
                                      {files
                                        .filter(
                                          (f) =>
                                            // Hide signature files (show=0)
                                            f.show !== 0 &&
                                            (current_booking?.joint_application !=
                                              1 ||
                                              applicantFilter === "all" ||
                                              (applicantFilter === "main" &&
                                                !f.is_joint) ||
                                              (applicantFilter === "joint" &&
                                                f.is_joint)) &&
                                            (!searchQuery ||
                                              f.name
                                                ?.toLowerCase()
                                                .includes(
                                                  searchQuery.toLowerCase(),
                                                )),
                                        )
                                        .sort((a, b) =>
                                          sortBy === "date"
                                            ? new Date(b.created_at) -
                                              new Date(a.created_at)
                                            : (a.name || "").localeCompare(
                                                b.name || "",
                                              ),
                                        )
                                        .map((file) => (
                                          <FileDisplay
                                            file={file}
                                            key={file.id}
                                            setShowFile={setShowFile}
                                            showFile={showFile}
                                            setFileId={setFileId}
                                            fileId={fileId}
                                            onScreen={false}
                                            currentPreview={fileId}
                                            onFileChange={fetchFiles}
                                            showJointToggle={
                                              current_booking?.joint_application ==
                                              1
                                            }
                                            compactMode={showFile}
                                          />
                                        ))}
                                    </ul>
                                  </>
                                )}
                              </dd>
                            ) : loading ? (
                              <div className="flex items-center justify-center py-8">
                                <FontAwesomeIcon
                                  className="h-6 w-6 animate-spin text-gray-400"
                                  icon={faLoader}
                                />
                                <span className="ml-2 text-gray-500">
                                  Loading files...
                                </span>
                              </div>
                            ) : (
                              <p>No files have been uploaded</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </ErrorBoundary>
      </Dialog>
    </Transition.Root>
  );
}
